import { ReactNode } from 'react';
import { useAuth } from '@/context/AuthContext';
import { UserRole } from '@/features/users/types';
import { Navigate } from '@tanstack/react-router';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: UserRole | UserRole[];
  requiredPermission?: string;
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requiredRoles,
  requiredPermission,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasRole, hasPermission } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return <div>Loading...</div>;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} />;
  }

  // Check role-based access if required
  if (requiredRoles && !hasRole(requiredRoles)) {
    return <Navigate to="/unauthorized" />;
  }

  // Check permission-based access if required
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/unauthorized" />;
  }

  // User is authenticated and has required role/permission
  return <>{children}</>;
}
